<template>
    <div
        class="ui-dropdown d-flex align-items-center"
        :class="[classes, openDirection]"
        tabindex="0"
        @blur="onBlur"
        @keydown.tab="handleKeyTab"
        @keydown.down.prevent="handleKeyDown"
        @keydown.up.prevent="handleKeyUp"
        @keydown.enter="handleEnter"
        @keyup.enter="handleEnterUp"
        @keydown="anyKey"
        @click="handleClick()">
        <div
            class="ui-dropdown-button-content d-flex justify-content-between align-items-center w-100"
            role="button"
            aria-haspopup="listbox"
        >
            <div class="ui-dropdown-button-content-slot">
                <slot name="button-content" :item="value" />
            </div>
            <UIIcon :name="['fal', 'chevron-down']" class="d-flex dropdown-chevron ml-auto"></UIIcon>
        </div>
        <div
            v-show="open"
            class="ui-dropdown-item-wrapper"
            role="listbox"
            :aria-expanded="open"
            :aria-activedescendant="`option-${selectedIndex}`"
        >
            <div
                v-for="(item, index) of items"
                :id="`option-${index}`"
                ref="dropdownItem"
                :key="index"
                class="ui-dropdown-item d-flex align-items-center"
                role="option"
                :aria-selected="selectedIndex === index"
                :class="[
                    getSelectedIndex(value) === index ? 'selected' : '',
                    selectedIndex === index ? 'focus-state' : '',
                ]"
                :tabindex="open ? '0' : '-1'"
                @click="handleClick(item)"
            >
                <slot name="dropdown-content" :item="item" />
            </div>
        </div>
    </div>
</template>

<script>
import { UIIcon } from '@dundle/ui/components';

export default {
    components: {
        UIIcon,
    },
    props: {
        items: Array,
        value: Object,
        search: {
            type: Function,
            default: item => item,
        },
    },

    data() {
        return {
            open: false,
            enterIsPressed: false,
            openDirection: 'open-bottom',
            selectedIndex: this.getSelectedIndex(this.value),
        };
    },

    computed: {
        classes() {
            return this.open ? 'active' : '';
        },

        indexOfItems() {
            const mappedItems = this.items.map(this.search);

            const index = {};

            mappedItems
                .filter(mappedItem => typeof mappedItem === 'string')
                .forEach((mappedItem, i) => {
                    const firstChar = mappedItem[0];

                    if (firstChar in index) {
                        index[firstChar.toUpperCase()].push(i);
                    } else {
                        index[firstChar.toUpperCase()] = [i];
                    }
                });

            return index;
        },
    },

    methods: {
        getSelectedIndex(value) {
            if (value) {
                const selectedItem = this.items.find(item => {
                    if (item) {
                        if (item === value) return item;
                        if (item.value && item.value === value.value) return item;
                        if (item.id && item.id === value.id) return item;
                    } else {
                        return 0;
                    }
                });
                return this.items.indexOf(selectedItem);
            } else {
                return 0;
            }
        },

        handleClick(item) {
            if (item) this.setValue(item);
            else this.toggleDropdown();
        },

        toggleDropdown() {
            this.setDropdownDirection();
            this.open = !this.open;
        },

        closeDropdown() {
            this.open = false;
        },

        openDropdown() {
            this.setDropdownDirection();
            this.open = true;
            this.setSelected(this.selectedIndex);
        },

        setDropdownDirection() {
            const { height, top, bottom } = this.$el.getBoundingClientRect();

            const contentHeight = Math.min(this.items.length, 4.5) * height;

            const windowHeight = document.documentElement.clientHeight;

            const distanceToBottom = windowHeight - bottom;
            const distanceToTop = top - 60;

            if (distanceToTop > distanceToBottom) {
                this.openDirection = 'open-top';
            } else {
                this.openDirection = 'open-bottom';
            }
        },

        onBlur(e) {
            if (!e.relatedTarget) {
                this.closeDropdown();
            }
            if (e.relatedTarget && e.relatedTarget.getAttribute('role') !== 'option') {
                this.closeDropdown();
            }
            if (e.relatedTarget && e.relatedTarget.classList.contains('ui-dropdown-search-button')) {
                this.closeDropdown();
            }
        },

        handleEnter(e) {
            if (!this.enterIsPressed) {
                this.enterIsPressed = true;

                if (this.open) {
                    this.setValue(this.items[this.selectedIndex]);
                    this.closeDropdown();
                } else {
                    this.openDropdown();
                }
            }
        },

        handleEnterUp() {
            this.enterIsPressed = false;
        },

        handleKeyUp(e) {
            if (this.open && this.selectedIndex > 0) {
                this.setSelected(this.selectedIndex - 1);
            }
        },
        handleKeyDown(e) {
            if (this.open && this.selectedIndex < this.items.length - 1) {
                this.setSelected(this.selectedIndex + 1);
            }
        },

        setValue(item) {
            this.$emit('input', item);
            this.$emit('change', item);
            const itemIndex = this.getSelectedIndex(item);
            this.setSelected(itemIndex);
        },

        setSelected(index) {
            this.selectedIndex = index;

            if (this.open) {
                this.$nextTick(() => {
                    if (this.$refs.dropdownItem[this.selectedIndex])
                        this.$refs.dropdownItem[this.selectedIndex].scrollIntoView({ block: 'center' });
                });
            }
        },
        handleKeyTab(e) {
            if (this.open) {
                e.preventDefault();
                if (e.shiftKey) {
                    if (this.selectedIndex === 0) {
                        this.closeDropdown();
                    } else {
                        this.handleKeyUp();
                    }
                } else {
                    this.handleKeyDown();
                }
            }
        },
        anyKey(e) {
            if (e.key && e.key.match(/^[a-z]$/i)) {
                const character = e.key.toUpperCase();

                if (character in this.indexOfItems) {
                    const tempIndex = this.indexOfItems[character];

                    const nextIndex = tempIndex.find(index => index > this.selectedIndex) || tempIndex[0];

                    this.setSelected(nextIndex);
                }
            }
            // key = Escape
            if (e.keyCode === 27) {
                this.closeDropdown();
            }
            // key = End
            if (e.keyCode == 35) {
                e.preventDefault();
                this.setSelected(this.items.length - 1);
            }
            // key = Home
            if (e.keyCode == 36) {
                e.preventDefault();
                this.setSelected(0);
            }
        },
    },
};
</script>
<style lang="scss" scoped>
.ui-dropdown {
    position: relative;
    padding: 0.5rem 1rem;
    border: 1px solid $color-grey-3;
    border-radius: 3px;
    user-select: none;
    background: $color-form-bg;
    line-height: 1.75;
    min-height: 3rem;

    &:hover {
        cursor: pointer;
    }

    @media only screen and (max-width: 1024px) {
        min-height: 3.33rem;
    }

    .ui-dropdown-button-content {
        .dropdown-chevron {
            width: 18px;
            height: 18px;
            margin-left: auto;
            color: $color-grey-6;
            transition: transform 0.2s;
            transform-style: preserve-3d;
        }
    }

    &.active .ui-dropdown-button-content {
        .dropdown-chevron {
            color: $color-grey-6;
            transform: rotateX(-180deg);
        }
    }

    &.open-top .ui-dropdown-item-wrapper {
        bottom: 110%;
    }
    &.open-bottom .ui-dropdown-item-wrapper {
        top: 110%;
        outline: none;
    }
    .ui-dropdown-item-wrapper {
        position: absolute;
        max-height: 16rem;
        overflow-y: scroll;
        border-radius: 4px;
        z-index: 5;
        left: -1px;
        right: -1px;
        box-shadow: 0 8px 16px 0 rgba(0, 0, 0, 0.41);

        .ui-dropdown-item {
            min-height: 43px;
            padding: 0.5rem 1rem;
            line-height: 1.75;
            background: $color-form-bg;
            min-height: 3rem;
            border-bottom: 1px solid $color-grey-2;
            &.selected {
                background: $color-grey-2;
            }
            &.focus-state {
                background: $color-primary;
                color: white;
            }
            &:hover {
                cursor: pointer;
                background: $color-primary;
                color: white;
            }
            @media only screen and (max-width: 1024px) {
                min-height: 3.33rem;
            }
        }

        &::-webkit-scrollbar-track {
            border-radius: 3.5px;
            background-color: $color-form-bg;
        }

        &::-webkit-scrollbar {
            width: 7px;
            border-radius: 4px;
            background-color: $color-form-bg;
        }

        &::-webkit-scrollbar-thumb {
            border-radius: 3.5px;
            background-color: $color-grey-3;
        }
    }
}
</style>
